// A static, non-interactive clone of a route's rendered DOM, used as a transition layer — cloned rather than
// rasterized, so there is no html2canvas-style dependency and no async paint. Never exists during SSR or hydration.

// Also used by the cache to re-normalize a layer it clones after the controller has written transforms onto it.
export const LAYER_STYLE =
  'position:absolute;inset:0;overflow:hidden;pointer-events:none;background:var(--background);' +
  'will-change:transform;contain:layout paint;'

// `applyScroll` must be called AFTER the layer is in the document: scroll offsets do not stick on a detached node.
// What a plain cloneNode gets wrong: duplicate id/name shadowing the live page's, blank canvases, and scroll offsets.
// A SELECTOR rather than a walk, and that is the point: pairing querySelectorAll('*') over source and clone is tens
// of thousands of node visits on an admin table, on the animation's critical path, to fix up a handful of elements.
// Scroll containers are found by their Tailwind overflow-* class; one styled inline would snapshot un-scrolled.
const NEEDS_REPAIR = '[id],[name],canvas,[class*="overflow-"]'

export function captureSnapshot(source: HTMLElement): { layer: HTMLElement; applyScroll: () => void } {
  const layer = document.createElement('div')
  layer.setAttribute('data-rt-layer', 'snapshot')
  layer.style.cssText = LAYER_STYLE

  const clone = source.cloneNode(true) as HTMLElement
  clone.style.width = '100%'
  clone.style.height = '100%'

  // The same query over identical trees, so the two lists line up index for index.
  const srcNodes = source.querySelectorAll<HTMLElement>(NEEDS_REPAIR)
  const cloneNodes = clone.querySelectorAll<HTMLElement>(NEEDS_REPAIR)
  const scrolls: Array<{ el: HTMLElement; top: number; left: number }> = []
  for (let i = 0; i < srcNodes.length; i++) {
    const s = srcNodes[i]
    const c = cloneNodes[i]
    if (!s || !c) continue
    c.removeAttribute('id')
    c.removeAttribute('name')
    if (s.scrollTop || s.scrollLeft) scrolls.push({ el: c, top: s.scrollTop, left: s.scrollLeft })
    if (s instanceof HTMLCanvasElement && c instanceof HTMLCanvasElement) {
      // Best-effort: copy the rendered bitmap (a clone starts blank). Fails silently on a tainted canvas.
      try {
        c.getContext('2d')?.drawImage(s, 0, 0)
      } catch {
        /* cross-origin/tainted — leave the clone blank */
      }
    }
  }

  layer.appendChild(clone)
  return {
    layer,
    applyScroll: () => {
      for (const { el, top, left } of scrolls) {
        el.scrollTop = top
        el.scrollLeft = left
      }
    },
  }
}
